"use client";

import { Lock } from "lucide-react";
import { ActivityPost } from "@/components/feed/activity-post";
import { ActivitySessionCard } from "@/components/feed/activity-session-card";
import { SocialFeedItemCard } from "@/components/feed/social-feed-item";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import type { FeedEntityQuoteTarget } from "@/lib/feed/feed-entity-quote";
import { cn } from "@/lib/utils";

const ENTITY_NOUN: Record<FeedEntityQuoteTarget["entityType"], string> = {
  activity_post: "activity",
  activity_session: "session",
  hiive_membership: "update",
  event_booking_attendee: "update",
};

/**
 * A read-only instance of the real source card. The source cards own their
 * presentation; this component only supplies the quote boundary and disables
 * their feed action row so an embed cannot recursively quote itself. When the
 * viewer isn't in the entity's audience it renders a restricted placeholder
 * (never the entity's content) with a link to the owner's profile.
 */
export function FeedEntityQuoteEmbed({
  target,
  compact = false,
}: {
  target: FeedEntityQuoteTarget;
  compact?: boolean;
}) {
  if ("restricted" in target) {
    const noun = ENTITY_NOUN[target.entityType] ?? "post";
    const reason =
      target.reason === "close_friends"
        ? `Only ${target.author.displayName}'s close friends can see this ${noun}.`
        : `Only ${target.author.displayName}'s buddies can see this ${noun}.`;
    const profileHref = target.author.username
      ? `/profile/${target.author.username}`
      : null;

    return (
      <div
        className="overflow-hidden rounded-xl border border-border bg-background"
        data-testid={`feed-entity-quote-embed-restricted-${target.entityType}`}
      >
        <div
          className={cn("flex items-start gap-2.5", compact ? "p-2.5" : "p-3")}
        >
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-alt text-text-tertiary">
            <Lock className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text-secondary">{reason}</p>
            {profileHref ? (
              <BackAwareLink
                href={profileHref}
                onClick={(event) => event.stopPropagation()}
                className="mt-0.5 inline-block text-sm font-semibold text-brand hover:underline"
              >
                View @{target.author.username}'s profile
              </BackAwareLink>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-xl border border-border bg-background"
      data-compact={compact || undefined}
      data-testid={`feed-entity-quote-embed-${target.entityType}`}
    >
      {target.entityType === "activity_post" ? (
        <ActivityPost post={target.item} embedded suppressDetailLink={false} />
      ) : target.entityType === "activity_session" ? (
        <ActivitySessionCard event={target.item} embedded />
      ) : (
        <SocialFeedItemCard item={target.item} embedded />
      )}
    </div>
  );
}
